import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Camera, Loader2, Phone, Instagram, Check, Shield, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { useI18n } from "../lib/i18n";

const STEPS = ["name", "photo", "business_privacy", "friend_privacy", "whatsapp", "instagram"];

const DEFAULT_PRIVACY = {
  share_name: true,
  share_whatsapp: false,
  share_instagram: true,
  share_email: false,
  anonymous_mode: false,
  friends_share_checkins: true,
  friends_share_journey: false,
  friends_share_favorites: true,
};

export default function TravelerSignup() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    display_name: "",
    profile_photo: "",
    whatsapp: "",
    instagram: "",
    privacy_settings: { ...DEFAULT_PRIVACY },
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      const me = await getMe();
      if (!me) { navigate("/login"); return; }
      setUser(me);
      setForm((f) => ({
        ...f,
        display_name: me.display_name || "",
        profile_photo: me.profile_photo || "",
        whatsapp: me.whatsapp || "",
        instagram: me.instagram || "",
        privacy_settings: { ...DEFAULT_PRIVACY, ...(me.privacy_settings || {}) },
      }));
    })();
  }, [navigate]);

  const updatePrivacy = (key, value) =>
    setForm((f) => ({ ...f, privacy_settings: { ...f.privacy_settings, [key]: value } }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.urls.medium}`;
      setForm((f) => ({ ...f, profile_photo: url }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const persist = async (extra = {}) => {
    setSaving(true);
    try {
      await api.patch("/users/me", { ...extra });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      return false;
    } finally {
      setSaving(false);
    }
    return true;
  };

  const next = async () => {
    const cur = STEPS[step];
    if (cur === "name") {
      if (!form.display_name.trim()) { toast.error("Display name is required"); return; }
      if (!(await persist({ display_name: form.display_name.trim() }))) return;
    }
    if (cur === "photo" && form.profile_photo && form.profile_photo !== user?.profile_photo) {
      if (!(await persist({ profile_photo: form.profile_photo }))) return;
    }
    if (cur === "business_privacy" || cur === "friend_privacy") {
      if (!(await persist({ privacy_settings: form.privacy_settings }))) return;
    }
    if (cur === "whatsapp" && form.whatsapp.trim()) {
      if (!(await persist({ whatsapp: form.whatsapp.trim() }))) return;
    }
    if (cur === "instagram" && form.instagram.trim()) {
      if (!(await persist({ instagram: form.instagram.trim() }))) return;
    }
    if (step === STEPS.length - 1) {
      toast.success(t("traveler_signup_done_toast") || "Welcome to Habano");
      navigate("/", { replace: true });
      return;
    }
    setStep(step + 1);
  };

  const skip = () => setStep(step + 1 < STEPS.length ? step + 1 : step);

  if (!user) return null;

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <main data-testid="traveler-signup" className="min-h-screen pt-24 pb-12 px-5 sm:px-8 bg-background">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">Habano · Setup</div>
          <div className="text-[11px] font-mono text-foreground/45">{step + 1} / {STEPS.length}</div>
        </div>
        <div className="h-[2px] bg-foreground/10 rounded-full overflow-hidden">
          <div className="h-full bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className="mt-10" data-testid={`step-${STEPS[step]}`}>
          {STEPS[step] === "name" && <NameStep form={form} setForm={setForm} />}
          {STEPS[step] === "photo" && (
            <PhotoStep
              photo={form.profile_photo}
              uploading={uploading}
              onPick={() => fileRef.current?.click()}
              fileRef={fileRef}
              onFile={uploadPhoto}
            />
          )}
          {STEPS[step] === "business_privacy" && (
            <BusinessPrivacyStep privacy={form.privacy_settings} update={updatePrivacy} />
          )}
          {STEPS[step] === "friend_privacy" && (
            <FriendPrivacyStep privacy={form.privacy_settings} update={updatePrivacy} />
          )}
          {STEPS[step] === "whatsapp" && (
            <WhatsAppStep value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
          )}
          {STEPS[step] === "instagram" && (
            <InstagramStep value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} />
          )}
        </div>

        <div className="mt-12 flex items-center justify-between">
          <button
            data-testid="step-back"
            onClick={() => step > 0 && setStep(step - 1)}
            disabled={step === 0}
            className="btn-ghost disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
          </button>
          <button data-testid="step-next" onClick={next} disabled={saving} className="btn-gold py-2.5 px-5 text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step === STEPS.length - 1 ? <Check className="w-4 h-4" strokeWidth={1.5} /> : <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
            {step === STEPS.length - 1 ? "Finish" : "Continue"}
          </button>
        </div>
        {/* Skip link below Continue for optional steps so users never feel
            stuck on Instagram / WhatsApp / Photo. */}
        {["photo", "whatsapp", "instagram"].includes(STEPS[step]) && (
          <div className="mt-3 text-center">
            <button
              data-testid="step-skip"
              onClick={skip}
              className="text-foreground/55 hover:text-foreground transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, minHeight: 48, padding: "8px 16px" }}
            >
              {STEPS[step] === "instagram" ? (t("traveler_signup_skip_instagram") || "Skip — I'll add this later") : (t("traveler_signup_skip") || "Skip this step")}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

function NameStep({ form, setForm }) {
  return (
    <>
      <div className="eyebrow">Step 1</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 text-foreground">What should we <em className="text-gold not-italic">call you</em>?</h1>
      <p className="mt-3 text-foreground/65 text-sm">This is how you&apos;ll appear on check-ins, reviews, and to friends.</p>
      <label className="block mt-8">
        <div className="eyebrow mb-1">Display name *</div>
        <input
          data-testid="signup-name"
          autoFocus
          value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          className="form-input text-lg"
          placeholder="Ernesto"
        />
      </label>
    </>
  );
}

function PhotoStep({ photo, uploading, onPick, fileRef, onFile }) {
  return (
    <>
      <div className="eyebrow">Step 2 · Optional</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 text-foreground">Add a <em className="text-gold not-italic">profile photo</em></h1>
      <p className="mt-3 text-foreground/65 text-sm">Lazy-loaded, compressed into three sizes automatically.</p>

      <div className="mt-8 flex items-center gap-5">
        {photo ? (
          <img src={photo} alt="" className="w-28 h-28 rounded-full object-cover ring-1 ring-[rgba(232,184,75,0.5)]" />
        ) : (
          <span className="w-28 h-28 rounded-full grid place-items-center bg-[hsl(var(--surface-elevated))] border border-gold-soft">
            <Camera className="w-8 h-8 text-foreground/30" strokeWidth={1.25} />
          </span>
        )}
        <button data-testid="signup-photo-pick" onClick={onPick} disabled={uploading} className="btn-outline py-2 px-4 text-xs">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {uploading ? "Uploading…" : photo ? "Replace" : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="signup-photo-input" onChange={(e) => onFile(e.target.files?.[0])} />
      </div>
    </>
  );
}

const BUSINESS_TOGGLES = [
  ["share_name", "Share my name with businesses", "When a business sees your check-in, they see your name."],
  ["share_whatsapp", "Share my WhatsApp with businesses", "Required if you want them to be able to message you."],
  ["share_instagram", "Share my Instagram", "Off by default. Adds your handle to your reviewer profile."],
  ["share_email", "Share my email", "Off by default. Most travelers leave this off."],
  ["anonymous_mode", "Anonymous mode", 'Businesses see "Anonymous User #1234". Friends still see your real name.'],
];

function BusinessPrivacyStep({ privacy, update }) {
  return (
    <>
      <div className="eyebrow">Step 3 · Privacy</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 text-foreground">What businesses <em className="text-gold not-italic">see</em>?</h1>
      <p className="mt-3 text-foreground/65 text-sm">You can change any of these later in Privacy Settings.</p>

      <div className="mt-8 space-y-4">
        {BUSINESS_TOGGLES.map(([k, label, hint]) => (
          <ToggleRow
            key={k}
            tid={`toggle-${k}`}
            label={label}
            hint={hint}
            icon={k === "anonymous_mode" ? Shield : undefined}
            checked={!!privacy[k]}
            onChange={(v) => update(k, v)}
          />
        ))}
      </div>
    </>
  );
}

const FRIEND_TOGGLES = [
  ["friends_share_checkins", "Let friends see my check-ins", "Most recent check-in only — never live location."],
  ["friends_share_journey", "Let friends see my journey", "Your visited places list, in order."],
  ["friends_share_favorites", "Let friends see my favorites", "Your saved lounges, vendors, and rooftops."],
];

function FriendPrivacyStep({ privacy, update }) {
  return (
    <>
      <div className="eyebrow">Step 4 · Friends</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 text-foreground">What <em className="text-gold not-italic">friends</em> see?</h1>
      <p className="mt-3 text-foreground/65 text-sm">Friends are people you&apos;ve connected with via QR. Anyone else just sees your public reviews.</p>

      <div className="mt-8 space-y-4">
        {FRIEND_TOGGLES.map(([k, label, hint]) => (
          <ToggleRow key={k} tid={`toggle-${k}`} label={label} hint={hint} icon={UsersIcon} checked={!!privacy[k]} onChange={(v) => update(k, v)} />
        ))}
      </div>
    </>
  );
}

function WhatsAppStep({ value, onChange }) {
  return (
    <>
      <div className="eyebrow">Step 5 · Optional</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 text-foreground">Connect your <em className="text-gold not-italic">WhatsApp</em></h1>
      <div className="mt-5 lux-card p-5 text-sm text-foreground/80 leading-relaxed border-gold-soft">
        WhatsApp is used to communicate with businesses and vendors. Nothing is sent without your action.
      </div>
      <label className="block mt-6">
        <div className="eyebrow mb-1">WhatsApp number (E.164)</div>
        <div className="relative">
          <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" strokeWidth={1.5} />
          <input
            data-testid="signup-whatsapp"
            type="tel"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="+5355551234"
            className="form-input pl-10"
          />
        </div>
      </label>
    </>
  );
}

function InstagramStep({ value, onChange }) {
  return (
    <>
      <div className="eyebrow">Step 6 · Optional</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 text-foreground">Connect your <em className="text-gold not-italic">Instagram</em></h1>
      <p className="mt-3 text-foreground/65 text-sm">Used only on your reviewer profile, if you opted in to share it.</p>
      <label className="block mt-8">
        <div className="eyebrow mb-1">Instagram handle</div>
        <div className="relative">
          <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" strokeWidth={1.5} />
          <input
            data-testid="signup-instagram"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="ernesto.cuba"
            className="form-input pl-10"
          />
        </div>
      </label>
    </>
  );
}

function ToggleRow({ tid, label, hint, icon: Icon, checked, onChange }) {
  return (
    <div className="lux-card p-4 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />}
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">{label}</div>
          <div className="text-xs text-foreground/55 leading-snug mt-0.5">{hint}</div>
        </div>
      </div>
      <Switch data-testid={tid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
